/**
 * Super-admin · Existing projects + progress.
 *
 * Cross-tenant rollup of every project the Atlas coding agent is working: how
 * many backlog tasks are done vs pending, and who has completed how many. Expand
 * a row to see the per-employee breakdown. Read-only status tracking.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type { ProjectDocView, ProjectProgress, TasksImportResult } from "@/lib/types";

// The full known doc set. `field` is the multipart field the /docs endpoint
// expects for the markdown docs; tasks.xlsx goes to the /tasks-import endpoint.
const KNOWN_DOCS: { type: string; label: string; accept: string; field?: string }[] = [
  { type: "brd", label: "BRD.md", accept: ".md,.markdown,.txt", field: "brd" },
  { type: "architecture", label: "architecture.md", accept: ".md,.markdown,.txt", field: "architecture" },
  { type: "code_helper", label: "code-helper.md", accept: ".md,.markdown,.txt", field: "code_helper" },
  { type: "tasks_xlsx", label: "tasks.xlsx", accept: ".xlsx" },
];

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Open a native file picker and resolve with the chosen file (or null).
function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

export function ExistingProjectsProgressPage() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ProjectDocView | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  // Per-(project:doc) upload state + last success note.
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["superadmin-projects-progress"],
    queryFn: async () =>
      (await api.get<ProjectProgress[]>("/superadmin/projects/progress")).data,
    refetchInterval: 30_000,
  });
  const projects = query.data ?? [];

  // Replace/add a project doc via the existing endpoints (markdown docs upsert;
  // tasks.xlsx re-imports into the backlog idempotently).
  const replaceDoc = async (
    projectId: string,
    doc: (typeof KNOWN_DOCS)[number],
  ) => {
    setDocError(null);
    setNote(null);
    const file = await pickFile(doc.accept);
    if (!file) return;
    const key = `${projectId}:${doc.type}`;
    setBusy(key);
    try {
      const form = new FormData();
      if (doc.type === "tasks_xlsx") {
        form.append("file", file, file.name);
        const res = (
          await api.post<TasksImportResult>(
            `/superadmin/projects/${projectId}/tasks-import`,
            form,
          )
        ).data;
        const parts = [
          res.created ? `${res.created} added` : null,
          res.skipped ? `${res.skipped} already present` : null,
          res.deferred ? `${res.deferred} pending assignment` : null,
        ].filter(Boolean);
        setNote(`tasks.xlsx imported${parts.length ? ` — ${parts.join(", ")}` : ""}.`);
      } else {
        form.append(doc.field ?? doc.type, file, file.name);
        await api.post(`/superadmin/projects/${projectId}/docs`, form);
        setNote(`${doc.label} replaced.`);
      }
      await queryClient.invalidateQueries({ queryKey: ["superadmin-projects-progress"] });
    } catch (e) {
      setDocError(extractErrorMessage(e, `Could not upload ${doc.label}.`));
    } finally {
      setBusy(null);
    }
  };

  // Fetch a doc: markdown opens in the viewer; the xlsx downloads via presign.
  const openDoc = async (projectId: string, docType: string) => {
    setDocError(null);
    try {
      const view = (
        await api.get<ProjectDocView>(`/superadmin/projects/${projectId}/docs/${docType}`)
      ).data;
      if (view.download_url) {
        window.open(view.download_url, "_blank", "noopener");
      } else {
        setViewing(view);
      }
    } catch (e) {
      setDocError(extractErrorMessage(e, "Could not open the document."));
    }
  };

  // Group by tenant.
  const byTenant = new Map<string, ProjectProgress[]>();
  for (const p of projects) {
    const k = p.tenant_name ?? "Unassigned";
    byTenant.set(k, [...(byTenant.get(k) ?? []), p]);
  }

  return (
    <div style={{ maxWidth: 980 }}>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Existing projects
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Backlog progress across every project — tasks completed vs pending, and
          who has done how many. Atlas works one task per day, rotating through
          each project's team.
        </p>
      </header>

      {docError && <div role="alert" style={errBar}>{docError}</div>}
      {note && <div style={okBar}>{note}</div>}

      {query.isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : projects.length === 0 ? (
        <p style={{ color: "var(--mute)" }}>No projects yet.</p>
      ) : (
        [...byTenant.entries()].map(([tName, ps]) => (
          <div key={tName} style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--body-mid)", marginBottom: 8 }}>
              {tName}
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {ps.map((p) => {
                const isOpen = expanded === p.project_id;
                const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
                return (
                  <div key={p.project_id} style={card}>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : p.project_id)}
                      style={rowBtn}
                    >
                      <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: p.color }} />
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--mute)", width: 90 }}>{p.key}</span>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, textAlign: "left" }}>{p.name}</span>
                      {p.github_repo && (
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--mute)" }}>{p.github_repo}</span>
                      )}
                      <span style={{ fontSize: 12.5, color: "var(--body-mid)" }}>
                        {p.done}/{p.total} done
                      </span>
                      <span aria-hidden style={{ color: "var(--mute)", fontSize: 12 }}>{isOpen ? "▾" : "▸"}</span>
                    </button>

                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
                      <div style={barTrack}>
                        <div style={{ ...barFill, width: `${pct}%`, background: p.color }} />
                      </div>
                      <span style={{ fontSize: 12, color: "var(--mute)", width: 36, textAlign: "right" }}>{pct}%</span>
                    </div>

                    <div style={statRow}>
                      <Stat label="Pending" value={p.todo} />
                      <Stat label="In progress" value={p.in_progress} />
                      <Stat label="In review" value={p.in_review} />
                      <Stat label="Done" value={p.done} />
                      <Stat label="Team" value={p.member_count} />
                    </div>

                    <div style={docsRow}>
                      <span style={docsLabel}>Documents</span>
                      {KNOWN_DOCS.map((doc) => {
                        const present = p.docs.find((d) => d.doc_type === doc.type);
                        const key = `${p.project_id}:${doc.type}`;
                        const uploading = busy === key;
                        return (
                          <span key={doc.type} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {present ? (
                              <button
                                type="button"
                                title={doc.type === "tasks_xlsx" ? "Download" : "View"}
                                onClick={() => openDoc(p.project_id, doc.type)}
                                style={docChip}
                              >
                                {present.filename}
                                <span aria-hidden style={{ color: "var(--mute)", marginLeft: 6 }}>
                                  {doc.type === "tasks_xlsx" ? "↓" : "↗"}
                                </span>
                              </button>
                            ) : (
                              <span style={{ ...docChip, color: "var(--mute)", cursor: "default" }}>
                                {doc.label}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => replaceDoc(p.project_id, doc)}
                              disabled={uploading}
                              title={present ? `Replace ${doc.label}` : `Add ${doc.label}`}
                              style={replaceBtn}
                            >
                              {uploading ? "…" : present ? "Replace" : "Add"}
                            </button>
                          </span>
                        );
                      })}
                    </div>

                    {isOpen && (
                      <div style={{ marginTop: 12, borderTop: "1px solid var(--hairline)", paddingTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", color: "var(--mute)", marginBottom: 8 }}>
                          Completed by employee
                        </div>
                        {p.by_employee.length === 0 ? (
                          <p style={{ color: "var(--mute)", fontSize: 13, margin: 0 }}>No tasks completed yet.</p>
                        ) : (
                          <div style={{ display: "grid", gap: 6 }}>
                            {p.by_employee.map((e) => (
                              <div key={e.user_id} style={empRow}>
                                <span style={{ flex: 1, fontSize: 13.5 }}>{e.display_name}</span>
                                <span style={{ fontSize: 12, color: "var(--mute)" }}>{e.email}</span>
                                <span style={{ fontSize: 13, fontWeight: 600, width: 64, textAlign: "right" }}>
                                  {e.done} task{e.done === 1 ? "" : "s"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {viewing && (
        <div style={modalOverlay} onClick={() => setViewing(null)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{viewing.filename}</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => downloadText(viewing.filename, viewing.content ?? "")}
                  style={smallBtn}
                >
                  Download
                </button>
                <button type="button" onClick={() => setViewing(null)} style={smallBtn}>
                  Close
                </button>
              </div>
            </div>
            <pre style={modalBody}>{viewing.content}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label: text, value }: { label: string; value: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 18, fontWeight: 600 }}>{value}</span>
      <span style={{ fontSize: 11, color: "var(--mute)" }}>{text}</span>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 16,
};
const rowBtn: CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, width: "100%",
  background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--ink)",
};
const barTrack: CSSProperties = {
  flex: 1, height: 8, borderRadius: 9999, background: "var(--hairline)", overflow: "hidden",
};
const barFill: CSSProperties = { height: "100%", borderRadius: 9999, transition: "width 0.3s" };
const statRow: CSSProperties = {
  display: "flex", gap: 28, marginTop: 14,
};
const empRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, padding: "7px 10px",
  border: "1px solid var(--hairline)", borderRadius: 6, background: "var(--canvas)",
};
const docsRow: CSSProperties = {
  display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 14,
};
const docsLabel: CSSProperties = {
  fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", color: "var(--mute)",
};
const docChip: CSSProperties = {
  display: "inline-flex", alignItems: "center", fontFamily: "var(--font-mono)", fontSize: 12,
  padding: "5px 10px", borderRadius: 4, border: "1px solid var(--hairline)",
  background: "var(--canvas)", color: "var(--ink)", cursor: "pointer",
};
const errBar: CSSProperties = {
  padding: "9px 12px", fontSize: 13, color: "#ee1d36", marginBottom: 14,
  background: "rgba(238,29,54,0.06)", border: "1px solid rgba(238,29,54,0.3)", borderRadius: 6,
};
const okBar: CSSProperties = {
  padding: "9px 12px", fontSize: 13, color: "#047a18", marginBottom: 14,
  background: "rgba(0,215,34,0.08)", border: "1px solid rgba(0,215,34,0.3)", borderRadius: 6,
};
const replaceBtn: CSSProperties = {
  background: "var(--surface-1)", border: "1px solid var(--hairline)", borderRadius: 4,
  padding: "3px 8px", fontSize: 11, color: "var(--body-mid)", cursor: "pointer",
};
const modalOverlay: CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(8,8,8,0.45)", display: "flex",
  alignItems: "center", justifyContent: "center", padding: 24, zIndex: 50,
};
const modalCard: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 8,
  width: "min(820px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column",
  overflow: "hidden",
};
const modalHeader: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  padding: "14px 16px", borderBottom: "1px solid var(--hairline)",
};
const modalBody: CSSProperties = {
  margin: 0, padding: 16, overflow: "auto", fontFamily: "var(--font-mono)", fontSize: 12.5,
  lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "var(--ink)",
};
const smallBtn: CSSProperties = {
  background: "var(--surface-2)", border: "1px solid var(--hairline)", borderRadius: 4,
  padding: "6px 12px", fontSize: 13, color: "var(--ink)", cursor: "pointer",
};
